//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { adminAPI } from "./adminAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type AdminsDetails,
  type CreateAdminInput,
  type UpdateAdminInput,
} from "./types/types";

//* Import types from CSVAdmin-validator
import {
  type CSVAdmin,
  type CSVAdminProcessingError,
  type CSVAdminProcessingResult,
} from "../../utils/csv/csv-validatorAdmin";

export const useAdmins = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "admins",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      adminAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const admins = data?.admins || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createAdminMutation = useMutation({
    mutationFn: (adminData: CreateAdminInput) =>
      adminAPI.create(adminData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ id, adminData }: UpdateAdminInput) =>
      adminAPI.update(id, adminData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const deleteAdminMutation = useMutation({
    mutationFn: (id: string) => adminAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const getAdminById = useCallback(
    async (id: string): Promise<AdminsDetails | null> => {
      try {
        const adminDetails = await adminAPI.getById(id, token);
        return adminDetails;
      } catch (err) {
        console.error("Error fetching admin by ID:", err);
        return null;
      }
    },
    [token]
  );

  const processCSVAdminMutation = useMutation<
    CSVAdminProcessingResult,
    Error,
    CSVAdmin[]
  >({
    mutationFn: async (
      admins: CSVAdmin[]
    ): Promise<CSVAdminProcessingResult> => {
      try {
        const result = await adminAPI.createBatch(admins, token);

        return {
          success: result.success,
          errors: result.errors.map((err: any) => ({
            id: err.id,
            admin: {
              name: err.admin.name,
              email: err.admin.email,
              isValid: false,
              is_restore: err.is_restore,
            },
            error: err.error || "Unknown error",
          })),
          total: result.total,
        };
      } catch (error: any) {
        // Captura os erros retornados pelo backend
        let errorMessage = "Erro desconhecido";
        let errors: CSVAdminProcessingError[] = [];

        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          const backendError = error.response.data.error;
          errorMessage = backendError.message || errorMessage;

          // Se o backend retornar erros detalhados
          if (backendError.errors && Array.isArray(backendError.errors)) {
            errors = backendError.errors;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        return {
          success: 0,
          errors:
            errors.length > 0
              ? errors.map((err) => ({
                  id: err.id,
                  admin: {
                    name: err.admin?.name || "Nome não disponível",
                    email: err.admin?.email || "Email não disponível",
                    isValid: false,
                  },
                  error: err.error || errorMessage,
                  is_restore: err.admin.is_restore,
                }))
              : admins.map((admin, index) => ({
                  id: `error-${index}`,
                  admin: { ...admin, isValid: false },
                  error: errorMessage,
                  is_restore: false,
                })),
          total: admins.length,
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const restoreByBatchAdminMutation = useMutation({
    mutationFn: (id: string) => adminAPI.restoreByBatch(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  const restoreByEmailAdminMutation = useMutation({
    mutationFn: (email: string) => adminAPI.restoreByEmail(email, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });


  return {
    admins,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createAdminAsync: createAdminMutation.mutateAsync, 
    updateAdmin: updateAdminMutation.mutate,
    deleteAdmin: deleteAdminMutation.mutate,
    getAdminById,
    changePage,
    isCreating: createAdminMutation.isPending,
    isUpdating: updateAdminMutation.isPending,
    isDeleting: deleteAdminMutation.isPending,
    processCSVAdmin: processCSVAdminMutation.mutateAsync,
    isProcessingCSVAdmin: processCSVAdminMutation.isPending,
    CSVAdminError: processCSVAdminMutation.error,
    restoreByBatchAdmin: restoreByBatchAdminMutation.mutate,
    restoreByEmailAdmin: restoreByEmailAdminMutation.mutate,
  };
};
